/* global BigInt */
import React, { useEffect, useState } from "react";
import "./FlightRhythmPanel.css";

const STATUS_LABELS = {
  RESERVED: "Scheduled",
  ISSUING: "Preparing with platform",
  ISSUED: "Platform setup verified",
  CANCELLED: "Cancelled",
  EXPIRED: "Issuance window missed",
  RECONCILIATION_REQUIRED: "Review required",
};

export function formatFlightRhythmMoney(minor, currency) {
  if (minor == null) return "Not reported";
  try {
    if (!/^-?\d+$/.test(String(minor)) || !/^[A-Z]{3}$/.test(currency || "")) return "Not reported";
    const value = BigInt(String(minor));
    const negative = value < 0n;
    const magnitude = negative ? -value : value;
    const whole = (magnitude / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const fraction = (magnitude % 100n).toString().padStart(2, "0");
    return `${negative ? "−" : ""}${currency === "USD" ? "$" : `${currency} `}${whole}.${fraction}`;
  } catch (_error) {
    return "Not reported";
  }
}

function displayDate(value, timezone) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value || "Date unavailable");
  try {
    return date.toLocaleDateString(undefined, { dateStyle: "medium", timeZone: timezone || "UTC" });
  } catch (_error) {
    return date.toLocaleDateString();
  }
}

const platformLabel = (platform) => String(platform || "platform").replace(/_/g, " ");
const metric = (value) => value ?? "Not reported";

function CreativeAssignments({ assignments }) {
  if (!assignments?.length) return <p>Approved creative versions: Not available</p>;
  return <p>Approved creative versions: {assignments.map((id) => `#${id}`).join(", ")}</p>;
}

function ClientPeriodCard({ period, timezone }) {
  const creatives = period.creatives || [];
  return <article data-testid="flight-rhythm-period">
    <h3>{period.label} <span>{platformLabel(period.platform)}</span></h3>
    <p className="flight-rhythm-status">{STATUS_LABELS[period.status] || "Review required"}</p>
    <p>{displayDate(period.start, timezone)} – {displayDate(period.end, timezone)} (end exclusive)</p>
    <CreativeAssignments assignments={period.assignments} />
    <dl>
      <div><dt>Client budget allocation</dt><dd>{formatFlightRhythmMoney(period.allocatedMinor, period.currency)}</dd></div>
      <div><dt>Client-billed cost</dt><dd>{formatFlightRhythmMoney(period.spendMinor, period.currency)}</dd></div>
      <div><dt>Report freshness</dt><dd>{period.freshness === "CURRENT" ? "Current" : period.freshness === "STALE" ? "Needs refresh" : "Not yet reported"}</dd></div>
      <div><dt>Impressions</dt><dd>{metric(period.impressions)}</dd></div>
      <div><dt>Clicks</dt><dd>{metric(period.clicks)}</dd></div>
    </dl>
    {period.billingProvenance === "UNAVAILABLE" && <p>Client billing basis is unavailable, so provider net-media amounts are withheld.</p>}
    {creatives.some((row) => row.sourceKind === "synthetic_provider") && <p className="flight-rhythm-fixture">Offline demonstration · synthetic provider reporting</p>}
    {creatives.map((creative, index) => <p key={creative.renditions?.map((rendition) => rendition.renditionId).join(":") || index}>
      {creative.renditions?.length || "Unresolved"} creative renditions · {creative.metricDefinition || "Metric definition unavailable"} · {creative.attributionWindow || "Attribution unavailable"}
    </p>)}
  </article>;
}

export default function FlightRhythmPanel({ advertiserId, campaignId: selectedCampaignId, apiJson }) {
  const [campaigns, setCampaigns] = useState([]);
  const [choice, setChoice] = useState("");
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const campaignId = selectedCampaignId || choice;

  useEffect(() => {
    let current = true;
    setChoice("");
    setCampaigns([]);
    setReport(null);
    setError("");
    if (advertiserId && !selectedCampaignId) {
      apiJson(`/api/flight-rhythm/${advertiserId}/campaigns`)
        .then((data) => { if (current) setCampaigns(data.campaigns || []); })
        .catch((requestError) => { if (current) setError(requestError.message); });
    }
    return () => { current = false; };
  }, [advertiserId, selectedCampaignId, apiJson]);

  useEffect(() => {
    let current = true;
    setReport(null);
    setError("");
    if (!advertiserId || !campaignId) {
      setLoading(false);
      return () => { current = false; };
    }
    setLoading(true);
    apiJson(`/api/flight-rhythm/${advertiserId}/campaigns/${campaignId}`)
      .then((data) => { if (current) setReport(data.report || null); })
      .catch((requestError) => { if (current) setError(requestError.message); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [advertiserId, campaignId, refresh, apiJson]);

  return <section className="flight-rhythm-panel" aria-label="Flight Rhythm" data-testid="flight-rhythm-panel">
    <div className="flight-rhythm-heading">
      <div><h2>Flight Rhythm</h2><p>Approved creative periods and reported delivery for the selected advertiser.</p></div>
      {report && <button type="button" onClick={() => setRefresh((value) => value + 1)}>Refresh</button>}
    </div>
    {!selectedCampaignId && <label>Campaign
      <select value={choice} disabled={!advertiserId} onChange={(event) => setChoice(event.target.value)}>
        <option value="">Choose a campaign</option>
        {campaigns.map((campaign) => <option key={campaign.id} value={campaign.id}>{campaign.name}</option>)}
      </select>
    </label>}
    {loading && <p role="status">Loading approved periods…</p>}
    {error && <p role="alert">{error}</p>}
    {report && <>
      <p>{report.rhythm ? `${report.rhythm.timezone || "Approved campaign periods"}` : "No approved Flight Rhythm packages are available for this campaign."}</p>
      {!report.periods?.length && report.rhythm && <p>Approved period details are awaiting package preparation.</p>}
      <div className="flight-rhythm-periods">{(report.periods || []).map((period) => <ClientPeriodCard key={period.packageId} period={period} timezone={report.rhythm?.timezone} />)}</div>
      {!!report.limits?.length && <p>Some report rows cross a period boundary, use a different cost basis, or overlap another measurement. Their values are retained without estimating a split.</p>}
      <p className="flight-rhythm-note">Client amounts use the approved campaign billing basis. Campaign activation requires its separate approval. Cross-platform reach and attributed conversions are not deduplicated.</p>
    </>}
  </section>;
}
